"use client";

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { createPortal } from "react-dom";
import type { GamePreviewData } from "../api/getGamePreviews";

type GamePreview = GamePreviewData;

// Client-side cache for previews
const previewCache = new Map<number, GamePreview>();

/**
 * Seed the cache from data a CLIENT fetch returned (the home deck's widget
 * actions ship the previews of every row they render), so a list that was
 * not in the page's `__GAME_PREVIEWS__` script still opens its card with no
 * loading state. Same map, same instant first hover.
 */
export function seedPreviews(map: Record<number, GamePreview>) {
  for (const [id, preview] of Object.entries(map)) {
    previewCache.set(Number(id), preview);
  }
}

// Seed the cache once from the inline <script id="__GAME_PREVIEWS__"> tag
// injected server-side, so the first hover is instant without any fetch.
function seedCacheFromScript() {
  if (typeof document === "undefined") return;
  const el = document.getElementById("__GAME_PREVIEWS__");
  if (!el || el.dataset.seeded) return;
  try {
    const data = JSON.parse(el.textContent || "{}") as Record<
      string,
      GamePreview
    >;
    for (const [id, preview] of Object.entries(data)) {
      previewCache.set(Number(id), preview);
    }
    el.dataset.seeded = "1";
  } catch {
    // ignore parse errors
  }
}

function getReviewColor(percent: number): string {
  if (percent >= 95) return "#66c0f4";
  if (percent >= 80) return "#beee11";
  if (percent >= 70) return "#a4d007";
  if (percent >= 50) return "#c7d5e0";
  if (percent >= 20) return "#e35e1c";
  return "#c0392b";
}

function getReviewEmoji(percent: number): string {
  if (percent >= 95) return "😍";
  if (percent >= 80) return "😊";
  if (percent >= 70) return "🙂";
  if (percent >= 50) return "😐";
  if (percent >= 20) return "😞";
  return "😢";
}

// Genre color palette
const GENRE_COLORS: Record<string, string> = {
  FPS: "#4fc3f7",
  Shooter: "#4fc3f7",
  Action: "#e57373",
  RPG: "#ba68c8",
  Strategy: "#ffb74d",
  Simulation: "#81c784",
  Adventure: "#aed581",
  Casual: "#fff176",
  Indie: "#f48fb1",
  Racing: "#ff8a65",
  Sports: "#4db6ac",
  Puzzle: "#9575cd",
  Platformer: "#7986cb",
  "Massively Multiplayer": "#4dd0e1",
  Multiplayer: "#4dd0e1",
  "Free to Play": "#a5d6a7",
  "Early Access": "#ffcc80",
  Horror: "#ef5350",
  Survival: "#8d6e63",
  Competitive: "#ffd54f",
  "Open World": "#66bb6a",
  Sandbox: "#dce775",
};

function getGenreColor(genre: string): string {
  return GENRE_COLORS[genre] || "#8f98a0";
}

function formatPrice(
  priceFinal: number | null,
  priceInitial: number | null,
  discount: number | null,
  currency: string | null,
  isFree: boolean | null,
): ReactNode {
  if (isFree) return <span className="text-[#a4d007] font-semibold">Free</span>;
  if (priceFinal === null || priceFinal === 0) return null;

  const final = (priceFinal / 100).toFixed(2);
  const cur = currency || "USD";
  const symbol = cur === "USD" ? "$" : cur === "EUR" ? "€" : cur === "GBP" ? "£" : `${cur} `;

  if (discount && discount > 0 && priceInitial) {
    const initial = (priceInitial / 100).toFixed(2);
    return (
      <span className="flex items-center gap-2">
        <span className="bg-[#4c6b22] text-[#a4d007] text-xs font-bold px-1.5 py-0.5 rounded">
          -{discount}%
        </span>
        <span className="text-[#8f98a0] line-through text-xs">
          {symbol}{initial}
        </span>
        <span className="text-white font-semibold">
          {symbol}{final}
        </span>
      </span>
    );
  }

  return (
    <span className="text-white font-semibold">
      {symbol}{final}
    </span>
  );
}

function HoverCardMedia({ data }: { data: GamePreview }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = useState(false);
  const movie = data.movie;
  const hasDirectVideo = movie && (movie.webm || movie.mp4);
  const hasHls = movie && movie.hls;
  const hasVideo = hasDirectVideo || hasHls;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !movie) return;

    let hlsInstance: { destroy: () => void } | null = null;

    const onCanPlay = () => setVideoReady(true);
    video.addEventListener("canplay", onCanPlay);

    if (hasHls && !hasDirectVideo) {
      // Dynamically import hls.js only when needed
      import("hls.js").then(({ default: Hls }) => {
        if (!videoRef.current) return;
        if (Hls.isSupported()) {
          const hls = new Hls({ maxBufferLength: 10, maxMaxBufferLength: 20 });
          hlsInstance = hls;
          hls.loadSource(movie.hls!);
          hls.attachMedia(videoRef.current);
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            videoRef.current?.play().catch(() => {});
          });
        } else if (videoRef.current.canPlayType("application/vnd.apple.mpegurl")) {
          // Safari native HLS
          videoRef.current.src = movie.hls!;
          videoRef.current.play().catch(() => {});
        }
      });
    }

    return () => {
      video.removeEventListener("canplay", onCanPlay);
      hlsInstance?.destroy();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data.header_image && !hasVideo) return null;

  return (
    <div className="relative w-full h-[160px] bg-black overflow-hidden">
      {/* Static image — base layer / shown until video is ready */}
      {data.header_image && (
        <Image
          src={data.header_image}
          alt={data.name}
          fill
          className={`object-cover transition-opacity duration-500 ${
            videoReady ? "opacity-0" : "opacity-100"
          }`}
          sizes="340px"
          unoptimized
        />
      )}

      {/* Video — fades in when buffered */}
      {hasVideo && (
        <video
          ref={videoRef}
          autoPlay={hasDirectVideo ? true : undefined}
          muted
          loop
          playsInline
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
            videoReady ? "opacity-100" : "opacity-0"
          }`}
        >
          {hasDirectVideo && movie!.webm && (
            <source src={movie!.webm} type="video/webm" />
          )}
          {hasDirectVideo && movie!.mp4 && (
            <source src={movie!.mp4} type="video/mp4" />
          )}
        </video>
      )}
    </div>
  );
}

function HoverCardContent({ data }: { data: GamePreview }) {
  const hasReviews =
    data.review_count !== null &&
    data.review_count > 0 &&
    data.review_percent !== null;
  const hasPlayers = data.players_now !== null && data.players_now > 0;
  const hasFollowers = data.followers !== null && data.followers > 0;

  return (
    <div className="w-[340px] bg-[#1b2838] rounded-lg border border-[#2a475e] shadow-2xl overflow-hidden">
      <HoverCardMedia data={data} />

      <div className="p-3 space-y-2.5">
        {/* Game name */}
        <h4 className="text-white font-semibold text-base leading-tight truncate">
          {data.name}
        </h4>

        {/* Stats row */}
        {(hasReviews || hasPlayers || hasFollowers) && (
          <div className="flex items-center gap-3 text-xs">
            {hasReviews && (
              <span
                className="flex items-center gap-1"
                style={{ color: getReviewColor(data.review_percent!) }}
              >
                <span>{getReviewEmoji(data.review_percent!)}</span>
                <span className="font-semibold">
                  {data.review_percent!.toFixed(0)}%
                </span>
                <span className="text-[#8f98a0]">
                  ({data.review_count! >= 1000000
                    ? `${(data.review_count! / 1000000).toFixed(2)} M`
                    : data.review_count! >= 1000
                    ? `${(data.review_count! / 1000).toFixed(1)}k`
                    : data.review_count!.toLocaleString()}{" "}
                  reviews)
                </span>
              </span>
            )}
          </div>
        )}

        {/* Info grid */}
        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
          {data.developers && data.developers.length > 0 && (
            <>
              <span className="text-[#8f98a0]">Developer:</span>
              <span className="text-[#66c0f4] truncate">
                {data.developers[0]}
              </span>
            </>
          )}
          {data.release_date && (
            <>
              <span className="text-[#8f98a0]">Release Date:</span>
              <span className="text-white">{data.release_date}</span>
            </>
          )}
          {hasPlayers && (
            <>
              <span className="text-[#8f98a0]">Players Now:</span>
              <span className="text-[#a4d007] font-semibold">
                {data.players_now!.toLocaleString()}
              </span>
            </>
          )}
          {data.peak_24h !== null && data.peak_24h > 0 && (
            <>
              <span className="text-[#8f98a0]">24h Peak:</span>
              <span className="text-white">
                {data.peak_24h.toLocaleString()}
              </span>
            </>
          )}
          {hasFollowers && (
            <>
              <span className="text-[#8f98a0]">Follows:</span>
              <span className="text-white">
                {data.followers! >= 1000000
                  ? `${(data.followers! / 1000000).toFixed(2)} M`
                  : data.followers! >= 1000
                  ? `${(data.followers! / 1000).toFixed(1)}k`
                  : data.followers!.toLocaleString()}
              </span>
            </>
          )}
        </div>

        {/* Tags / genres */}
        {data.genres.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {data.genres.slice(0, 5).map((genre) => (
              <span
                key={genre}
                className="inline-flex items-center rounded px-2 py-0.5 text-[10px] font-medium border border-[#2a475e]/60 bg-[#0e1419]/80"
                style={{ color: getGenreColor(genre) }}
              >
                {genre}
              </span>
            ))}
          </div>
        )}

        {/* Tags */}
        {data.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {data.tags.map((tag) => (
              <span
                key={tag.tag_id}
                className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] text-[#8f98a0] bg-[#16202d] border border-[#2a475e]/40"
              >
                {tag.name}
              </span>
            ))}
          </div>
        )}

        {/* Price + Review label */}
        <div className="flex items-center justify-between pt-1 border-t border-[#2a475e]/60">
          <div className="text-xs">
            {formatPrice(
              data.price_final,
              data.price_initial,
              data.price_discount,
              data.price_currency,
              data.is_free,
            )}
          </div>
          {hasReviews && data.review_label && (
            <span
              className="text-[11px] font-medium px-2 py-0.5 rounded border"
              style={{
                color: getReviewColor(data.review_percent!),
                borderColor: `${getReviewColor(data.review_percent!)}40`,
                backgroundColor: `${getReviewColor(data.review_percent!)}15`,
              }}
            >
              {data.review_label}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

interface GameHoverCardProps {
  appid: number;
  children: ReactNode;
}

export function GameHoverCard({ appid, children }: GameHoverCardProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [data, setData] = useState<GamePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  // Reset hover state on route change (e.g. browser back navigation)
  useEffect(() => {
    setIsVisible(false);
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, [pathname]);

  const fetchPreview = useCallback(async () => {
    seedCacheFromScript();

    if (previewCache.has(appid)) {
      setData(previewCache.get(appid)!);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`/api/games/${appid}/preview`);
      if (res.ok) {
        const json = await res.json();
        previewCache.set(appid, json);
        setData(json);
      }
    } catch {
      // Silently fail
    } finally {
      setLoading(false);
    }
  }, [appid]);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const cardWidth = 340;
    const cardHeight = 450;
    const gap = 8;

    // Try right side first
    let left = rect.right + gap;
    let top = rect.top;

    // If it goes off the right edge, try left side
    if (left + cardWidth > window.innerWidth - 16) {
      left = rect.left - cardWidth - gap;
    }

    // If left side also doesn't work, position below
    if (left < 16) {
      left = Math.max(16, rect.left);
      top = rect.bottom + gap;
    }

    // Keep within vertical bounds
    if (top + cardHeight > window.innerHeight - 16) {
      top = Math.max(16, window.innerHeight - cardHeight - 16);
    }

    setPosition({ top, left });
  }, []);

  const handleMouseEnter = useCallback(() => {
    // Skip hover card on touch/mobile devices
    if (typeof window !== "undefined" && !window.matchMedia("(hover: hover)").matches) {
      return;
    }

    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    hoverTimerRef.current = setTimeout(() => {
      updatePosition();
      setIsVisible(true);
      fetchPreview();
    }, 400);
  }, [fetchPreview, updatePosition]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }

    hideTimerRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 150);
  }, []);

  const handleCardMouseEnter = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const handleCardMouseLeave = useCallback(() => {
    hideTimerRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 150);
  }, []);

  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="flex-1 flex flex-col"
      >
        {children}
      </div>

      {isVisible &&
        typeof window !== "undefined" &&
        createPortal(
          <div
            ref={cardRef}
            className="fixed z-[9999] pointer-events-auto"
            style={{
              animation: "hoverCardFadeIn 150ms ease-out",
              top: position.top,
              left: position.left,
            }}
            onMouseEnter={handleCardMouseEnter}
            onMouseLeave={handleCardMouseLeave}
          >
            {loading && !data ? (
              <div className="w-[340px] bg-[#1b2838] rounded-lg border border-[#2a475e] shadow-2xl p-6 flex items-center justify-center">
                <div className="flex items-center gap-2 text-[#8f98a0] text-sm">
                  <svg
                    className="animate-spin h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Loading...
                </div>
              </div>
            ) : data ? (
              <HoverCardContent data={data} />
            ) : null}
          </div>,
          document.body,
        )}
    </>
  );
}
